/**
 * MagicEdenBonusInfoTooltip Component
 *
 * Info icon with tooltip explaining the Magic Eden campaign bonus
 * Shows:
 * - What the bonus is (2x points for $ME stakers)
 * - Campaign duration
 * - Progress (X/20,000)
 * - Time remaining
 *
 * Used on the Points page next to "Your Points" heading
 */

import { useState } from 'react';
import { Info } from 'lucide-react';

import { useMagicEdenCampaign } from '@/hooks/useMagicEdenCampaign';

// Magic Eden brand color
const ME_COLOR = '#E42575';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} left`;
  }
  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} left`;
  }
  return 'Less than 1 hour left';
}

export function MagicEdenBonusInfoTooltip() {
  const { campaignData, isLoading } = useMagicEdenCampaign();
  const [isOpen, setIsOpen] = useState(false);

  // Only show if campaign is active and user is a staker
  if (isLoading || !campaignData?.isActive || !campaignData?.isMagicEdenStaker) {
    return null;
  }

  const isEligible = campaignData.isEligible;
  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;
  const progressPercent = (campaignData.userBonusEarned / campaignData.maxBonus) * 100;

  return (
    <div className="relative inline-flex">
      {/* Info Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200 hover:scale-110"
        style={{
          backgroundColor: 'rgba(228, 37, 117, 0.15)',
          border: `1px solid ${ME_COLOR}`,
        }}
      >
        <Info className="w-4 h-4" style={{ color: ME_COLOR }} />
      </button>

      {/* Tooltip Popover */}
      {isOpen && (
        <div
          className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-80 p-4 rounded-xl shadow-2xl z-50 animate-slide-in"
          style={{
            backgroundColor: '#343434',
            border: `2px solid ${ME_COLOR}`,
            boxShadow: `0 0 20px rgba(228, 37, 117, 0.3)`,
          }}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {/* Header with Icon */}
          <div className="flex items-center gap-2 mb-3">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                fill={ME_COLOR}
              />
            </svg>
            <h4
              className="text-base font-bold"
              style={{ color: ME_COLOR, fontFamily: 'Outfit, sans-serif' }}
            >
              Magic Eden $ME Staker Bonus
            </h4>
          </div>

          {isEligible ? (
            <>
              {/* Active Bonus Message */}
              <p className="text-sm mb-3" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                You're earning <strong style={{ color: ME_COLOR }}>2x points</strong> on chest openings as a $ME staker!
              </p>

              {/* Progress Section */}
              <div className="mb-3">
                <div className="flex justify-between text-xs mb-2" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                  <span>Bonus Earned</span>
                  <span style={{ color: ME_COLOR, fontWeight: 600 }}>{progress} pts</span>
                </div>
                {/* Progress Bar */}
                <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(228, 37, 117, 0.2)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(progressPercent, 100)}%`,
                      backgroundColor: ME_COLOR,
                    }}
                  />
                </div>
                {campaignData.remainingBonus > 0 && (
                  <p className="text-xs mt-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                    {campaignData.remainingBonus.toLocaleString()} pts remaining
                  </p>
                )}
              </div>

              {/* Duration Info */}
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(228, 37, 117, 0.1)',
                  border: `1px solid rgba(228, 37, 117, 0.3)`,
                }}
              >
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: ME_COLOR, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Cap Reached Message */}
              <p className="text-sm mb-3" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                You've reached the <strong style={{ color: ME_COLOR }}>maximum bonus</strong> for this campaign!
              </p>

              {/* Completed Badge */}
              <div
                className="p-3 rounded-lg mb-3 text-center"
                style={{
                  background: `linear-gradient(92deg, ${ME_COLOR} -4.02%, #FF6B9D 50.55%, ${ME_COLOR} 103.05%)`,
                }}
              >
                <p className="text-xs font-bold" style={{ color: '#FFFFFF', fontFamily: 'Outfit, sans-serif' }}>
                  20,000 Bonus Points Earned!
                </p>
              </div>

              {/* Duration Info */}
              <div
                className="p-3 rounded-lg"
                style={{
                  backgroundColor: 'rgba(228, 37, 117, 0.1)',
                  border: `1px solid rgba(228, 37, 117, 0.3)`,
                }}
              >
                <div className="flex justify-between items-center text-xs">
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: ME_COLOR, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
